import React from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import { HTTPError } from 'ky';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import { InfoField } from '@components-clean/core/info-field';
import Loading from '@components-clean/core/loading/loading';
import { TextAreaController } from '@components-clean/core/text-area';
import { ObservationType } from '@interfaces-clean/observations.interface';
import styles from '@pages-clean/admin/study/admin-data-views/data-views/details/style.module.scss';
import style from '@pages-clean/central-monitoring/monitoring-toolkit/tabs/dialogs/style.module.scss';
import { useTranslation } from '@hooks/index';
import ActionsApi from '@apis/bff-api/central-monitoring/actions';
import ObservationsApi from '@apis/bff-api/central-monitoring/observation';

export interface ActionDeleteDialogProps {
    recordId: string;
    onClose: () => void;
    studyRef: string;
    onDeleteHandler: (success: boolean, conflict?: boolean) => void;
    actionId: number;
    observationId?: string | null;
    type: ObservationType;
}

export type FormInputs = {
    deleteReason: string | null;
};

const DeleteActionDialog = ({
    recordId,
    onClose,
    studyRef,
    onDeleteHandler,
    actionId,
    observationId,
    type,
}: ActionDeleteDialogProps) => {
    const { t } = useTranslation('cm');
    const { trigger: deleteAction } = ActionsApi.useDelete(studyRef);
    const {
        control,
        handleSubmit,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<FormInputs>({
        mode: 'all',
        defaultValues: {
            deleteReason: null,
        },
    });

    const { data: observation, isValidating } = ObservationsApi.useGet(studyRef, observationId!);

    const onDelete = async (data: FormInputs) => {
        try {
            const response = await deleteAction({
                deletedReason: data.deleteReason,
                actionId,
            });
            onDeleteHandler(response);
        } catch (err) {
            const status = (err as HTTPError)?.response?.status;
            onDeleteHandler(false, status === 409);
        }
    };

    const maxChars = 350;

    // Only show up to 350 characters and then append ...
    const truncatedObs =
        observation?.observation && observation.observation.length > maxChars
            ? `${String(observation.observation).slice(0, maxChars)}...`
            : observation?.observation;

    // We need to append the additional height to the dialog depending on the observation length to avoid scroll bar. We need to show a maximum of
    // 5 lines for observation text which is approx 350 chars. Calculating the no of lines and then appending
    // 15 px height per line. 280 is the minimum required length. 65 px is if the number of lines is more than 5.

    const minDialogHeight = 280;
    const allowedAdditionalMaxHeight = 65;
    const approxCharsPerLine = 70;

    const calculatedHeight =
        (observationId?.length as unknown as number) > maxChars
            ? allowedAdditionalMaxHeight
            : ((observationId?.length as unknown as number) / approxCharsPerLine) * 15;

    const warningText = (
        <span className={cn('k-w-full k-d-flex k-flex-column k-gap-2', styles.fontSize)}>
            <div>{t('cm:actionGrid.deleteActionWarning')}</div>
            <span>
                <span className={style.textHighLightNoBold}>{truncatedObs}</span>
            </span>
        </span>
    );

    const dialogTitle =
        type === ObservationType.Controls
            ? `${t('cm:actionGrid.deleteControlsAction')}: ${recordId}`
            : `${t('cm:actionGrid.deleteAction')}: ${recordId}`;

    return (
        <DraggableDialog
            dataTestId="action-delete--warning"
            onSubmit={() => handleSubmit(onDelete)()}
            onClose={onClose}
            title={dialogTitle}
            initialHeight={minDialogHeight + calculatedHeight}
            initialWidth={520}
            primaryBtnLabel={t('common:delete')}
            primaryBtnDisabled={!isValid || !isDirty || isSubmitting}
            secondaryBtnDisabled={isSubmitting}
        >
            <div className="k-h-full k-w-full k-d-flex k-flex-column k-gap-2">
                {isValidating ? (
                    <Loading />
                ) : (
                    <>
                        <InfoField type="Warning" message={warningText} />
                        <TextAreaController
                            className={style.textAreaOverrides}
                            maxLength={3000}
                            control={control}
                            name="deleteReason"
                            required
                            data-testid="delete-action"
                            label={`${t('cm:deleteReasonLabel')} *`}
                            readOnly={isSubmitting}
                        />
                    </>
                )}
            </div>
        </DraggableDialog>
    );
};

export default DeleteActionDialog;
